import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogTitle, Button, Grid, DialogActions, CircularProgress, Typography, Box, Alert, Chip, Divider } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import ErrorIcon from "@mui/icons-material/Error";
import TerminView from "../TerminView";
import { TerminViewConstants } from "../../TerminView";
import { observer } from "mobx-react";
import { SeriesSwapAppointmentsDto, SwapValidationErrorDto } from "../../../../api-client";
import { AppointmentDto } from "../../../../api-client";
import { useSimpleSwapAppointments } from "../../../../stores/Appointment/appointment.mutation";
import PostSwapRxCompletionDialog, { CompletableRx } from "./PostSwapRxCompletionDialog";
import notificationStore from "../../../../stores/Notification/notificationStore";
import {
  filterChanged,
  groupSwapValidationErrors,
  nextSwapStep,
} from "./swapPreview.logic";

interface PreviewSwapDialogProps {
  open: boolean;
  onClose: () => void;
  seriesIds: string[];
  allBefore?: SeriesSwapAppointmentsDto[];
  allAfter?: SeriesSwapAppointmentsDto[];
  patientId: number;
  isLoading?: boolean;
  noChanges?: boolean;
  initialErrors?: SwapValidationErrorDto[];
  initialWarnings?: any[];
}

const PreviewSwapDialog: React.FC<PreviewSwapDialogProps> = observer(
  ({ open, onClose, seriesIds, allBefore, allAfter, patientId, isLoading = false, noChanges = false, initialErrors = [], initialWarnings = [] }) => {
    const { mutateAsync: simpleSwapAppointments } = useSimpleSwapAppointments();
    const [isSwapLoading, setIsSwapLoading] = React.useState(false);
    const [completionDialogOpen, setCompletionDialogOpen] = useState(false);
    const [completableRxs, setCompletableRxs] = useState<CompletableRx[]>([]);
    const [showResult, setShowResult] = useState(false);
    const [swapValidationErrors, setSwapValidationErrors] = useState<SwapValidationErrorDto[]>([]);
    const [perpetualStatuses, setPerpetualStatuses] = useState<string[]>([]);
    // New: preview phase - show errors before committing
    const [previewErrors, setPreviewErrors] = useState<SwapValidationErrorDto[]>([]);
    const [previewWarnings, setPreviewWarnings] = useState<any[]>([]);
    const [showPreviewErrors, setShowPreviewErrors] = useState(false);

    // Reset state when the dialog opens
    useEffect(() => {
      if (open) {
        setShowResult(false);
        setSwapValidationErrors([]);
        setPerpetualStatuses([]);
        setPreviewErrors([]);
        setPreviewWarnings([]);
        setShowPreviewErrors(false);
      }
    }, [open]);

    // Continue the post-swap flow once any blocking step has resolved. Only one
    // surfaces at a time (precedence top to bottom):
    // - completable Rxs   -> the actionable completion dialog
    // - validation errors -> the result modal (the perpetual cleanup, if any,
    //                        rides along in the combined banner)
    // - nothing           -> close; the success toast confirms the reorder,
    //                        including the perpetual-only cleanup case.
    const proceedAfterSwap = (
      completable: CompletableRx[],
      validationErrors: SwapValidationErrorDto[]
    ) => {
      switch (nextSwapStep(completable.length, validationErrors.length)) {
        case "completion":
          setCompletionDialogOpen(true);
          break;
        case "result":
          setShowResult(true);
          break;
        default:
          onClose();
      }
    };

    const handleSwapAppointments = async () => {
      setIsSwapLoading(true);
      try {
        // Step 1: Preview first (previewMode=true) to get errors/warnings
        const response = await simpleSwapAppointments({ seriesIds, previewMode: true });

        // Collect errors from compute and validation phases
        const allErrors: SwapValidationErrorDto[] = [
          ...(response.computeErrors?.map((e: any) => ({
            rxNumber: e.rxNumber || '',
            label: e.type || '',
            message: e.message || '',
          })) || []),
          ...(response.validationErrors?.map((e: any) => ({
            rxNumber: e.rxNumber || '',
            label: e.type || '',
            message: e.message || '',
          })) || []),
        ];

        const warnings = response.warnings || [];

        // Always show preview for user confirmation before committing
        setPreviewErrors(allErrors);
        setPreviewWarnings(warnings);
        setShowPreviewErrors(true);
      } catch (error) {
        const msg = (error as any)?.response?.data?.message || (error as any)?.message || "";
        notificationStore.showMessage(
          `Neuordnung fehlgeschlagen${msg ? `: ${msg}` : ""}`,
          "error",
          error
        );
        onClose();
      } finally {
        setIsSwapLoading(false);
      }
    };

    // User confirmed to proceed with errors
    const handleForceSwap = async () => {
      setIsSwapLoading(true);
      try {
        const response = await simpleSwapAppointments({ seriesIds, previewMode: false });

        const validationErrors: SwapValidationErrorDto[] = response.validationErrors?.map((e: any) => ({
          rxNumber: e.rxNumber || '',
          label: e.type || '',
          message: e.message || '',
        })) || [];
        setSwapValidationErrors(validationErrors);

        // Simple swap doesn't return completableRxs, so we leave it empty
        const completable: CompletableRx[] = [];
        setCompletableRxs(completable);

        // No perpetual status in simple swap response
        setPerpetualStatuses([]);

        // Clear preview state
        setShowPreviewErrors(false);
        setPreviewErrors([]);
        setPreviewWarnings([]);

        // One dialog at a time (see proceedAfterSwap).
        proceedAfterSwap(completable, validationErrors);
      } catch (error) {
        const msg = (error as any)?.response?.data?.message || (error as any)?.message || "";
        notificationStore.showMessage(
          `Neuordnung fehlgeschlagen${msg ? `: ${msg}` : ""}`,
          "error",
          error
        );
        onClose();
      } finally {
        setIsSwapLoading(false);
      }
    };

    const handleCompletionClose = () => {
      setCompletionDialogOpen(false);
      setCompletableRxs([]);

      // Continue to validation errors -> result modal; else done
      // (perpetual-only cleanup is reported via the success toast).
      proceedAfterSwap([], swapValidationErrors);
    };

    const handleResultClose = () => {
      onClose();
    };

    // Group swap-introduced validation errors by Rx so each prescription is
    // shown once with its issues in the result phase.
    const groupedErrors = groupSwapValidationErrors(swapValidationErrors);
    const groupedPreviewErrors = groupSwapValidationErrors(previewErrors);
    const groupedInitialErrors = groupSwapValidationErrors(initialErrors);
    const hasInitialErrors = initialErrors.length > 0 || initialWarnings.length > 0;

    return (
      <>
        <Dialog
          open={open && !completionDialogOpen}
          onClose={isLoading || isSwapLoading ? undefined : onClose}
          maxWidth="lg"
          fullWidth
        >
          <DialogTitle>
            {showResult 
              ? "Neuordnung – Validierungsfehler" 
              : hasInitialErrors
                ? "Neuordnung – Fehler erkannt"
                : "Neuordnung"}
          </DialogTitle>

          <DialogContent dividers>
            {isLoading ? (
              <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" gap={2} minHeight={120}>
                <CircularProgress />
                <Typography variant="body1" color="text.secondary">
                  Die Neuordnung wird berechnet…
                </Typography>
              </Box>
            ) : isSwapLoading ? (
              <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" gap={2} minHeight={120}>
                <CircularProgress />
                <Typography variant="body1" color="text.secondary">
                  Neuordnung wird durchgeführt und auf Validierungsfehler geprüft…
                </Typography>
              </Box>
            ) : showPreviewErrors ? (
              <>
                {/* Preview phase: show errors BEFORE committing, let user decide */}
                <Alert severity="warning" icon={<WarningAmberIcon />} sx={{ mb: 3, alignItems: "center" }}>
                  Bei der Neuordnung wurden folgende Probleme erkannt. Sie können trotzdem fortfahren oder abbrechen.
                </Alert>
                {groupedPreviewErrors.map((group, index) => (
                  <Box key={group.rxNumber + index} sx={{ mb: 2 }}>
                    <Box display="flex" alignItems="center" gap={1} sx={{ mb: 1 }}>
                      <Chip label={`${group.rxNumber}`} size="small" color="default" sx={{ fontWeight: 600, fontSize: "0.8125rem" }} />
                    </Box>
                    {group.items.map((item, i) => (
                      <Box key={item.label + i} sx={{ pl: 1, mb: 1 }}>
                        <Box display="flex" alignItems="center" gap={0.75}>
                          <ErrorIcon sx={{ color: "error.main", fontSize: "1.1rem" }} />
                          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                            {item.label}
                          </Typography>
                        </Box>
                        <Typography variant="body2" color="text.secondary" sx={{ pl: "1.5rem", fontSize: "0.8125rem", maxWidth: "135ch" }}>
                          {item.message}
                        </Typography>
                      </Box>
                    ))}
                    {index < groupedPreviewErrors.length - 1 && <Divider sx={{ mt: 1 }} />}
                  </Box>
                ))}
                {previewWarnings.length > 0 && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>Warnungen:</Typography>
                    {previewWarnings.map((warning, i) => (
                      <Box key={i} sx={{ pl: 1, mb: 1 }}>
                        <Box display="flex" alignItems="center" gap={0.75}>
                          <WarningAmberIcon sx={{ color: "warning.main", fontSize: "1.1rem" }} />
                          <Typography variant="body2" color="text.secondary">
                            {warning.message}
                          </Typography>
                        </Box>
                      </Box>
                    ))}
                  </Box>
                )}
              </>
            ) : showResult ? (
              <>
                {/* Single combined banner: in the result phase the perpetual cleanup
                    (if any) and the validation warning are shown together. The
                    perpetual-only case (no errors) is reported via the success toast. */}
                <Alert severity="warning" icon={<WarningAmberIcon />} sx={{ mb: 3, alignItems: "center" }}>
                  Die Neuordnung wurde durchgeführt.{" "}
                  {perpetualStatuses.length > 0 &&
                    "Nicht geplante Termine eines Dauerrezepts wurden automatisch entfernt. "}
                  Bei folgenden Rezepten sind dadurch neue Validierungsfehler aufgetreten. Bitte überprüfen Sie diese.
                </Alert>
                {groupedErrors.map((group, index) => (
                  <Box key={group.rxNumber + index} sx={{ mb: 2 }}>
                    <Box display="flex" alignItems="center" gap={1} sx={{ mb: 1 }}>
                      <Chip label={`${group.rxNumber}`} size="small" color="default" sx={{ fontWeight: 600, fontSize: "0.8125rem" }} />
                    </Box>
                    {group.items.map((item, i) => (
                      <Box key={item.label + i} sx={{ pl: 1, mb: 1 }}>
                        <Box display="flex" alignItems="center" gap={0.75}>
                          <ErrorIcon sx={{ color: "error.main", fontSize: "1.1rem" }} />
                          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                            {item.label}
                          </Typography>
                        </Box>
                        <Typography variant="body2" color="text.secondary" sx={{ pl: "1.5rem", fontSize: "0.8125rem", maxWidth: "135ch" }}>
                          {item.message}
                        </Typography>
                      </Box>
                    ))}
                    {index < groupedErrors.length - 1 && <Divider sx={{ mt: 1 }} />}
                  </Box>
                ))}
              </>
            ) : (!allBefore?.length && !allAfter?.length) ? (
              <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" py={6}>
                <Typography variant="h6" gutterBottom>
                  Termine neu ordnen
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  Die Termine werden chronologisch den Rezepten zugeordnet.
                </Typography>
              </Box>
            ) : (
              <>
                {/* Show errors/warnings at top of preview if any */}
                {hasInitialErrors && (
                  <Box sx={{ mb: 3 }}>
                    <Alert severity="warning" icon={<WarningAmberIcon />} sx={{ mb: 2, alignItems: "center" }}>
                      Bei der Neuordnung wurden folgende Probleme erkannt. Sie können trotzdem fortfahren oder abbrechen.
                    </Alert>
                    {groupedInitialErrors.map((group, index) => (
                      <Box key={group.rxNumber + index} sx={{ mb: 2 }}>
                        <Box display="flex" alignItems="center" gap={1} sx={{ mb: 1 }}>
                          <Chip label={`${group.rxNumber}`} size="small" color="default" sx={{ fontWeight: 600, fontSize: "0.8125rem" }} />
                        </Box>
                        {group.items.map((item, i) => (
                          <Box key={item.label + i} sx={{ pl: 1, mb: 1 }}>
                            <Box display="flex" alignItems="center" gap={0.75}>
                              <ErrorIcon sx={{ color: "error.main", fontSize: "1.1rem" }} />
                              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                {item.label}
                              </Typography>
                            </Box>
                            <Typography variant="body2" color="text.secondary" sx={{ pl: "1.5rem", fontSize: "0.8125rem", maxWidth: "135ch" }}>
                              {item.message}
                            </Typography>
                          </Box>
                        ))}
                        {index < groupedInitialErrors.length - 1 && <Divider sx={{ mt: 1 }} />}
                      </Box>
                    ))}
                    {initialWarnings.length > 0 && (
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>Warnungen:</Typography>
                        {initialWarnings.map((warning, i) => (
                          <Box key={i} sx={{ pl: 1, mb: 1 }}>
                            <Box display="flex" alignItems="center" gap={0.75}>
                              <WarningAmberIcon sx={{ color: "warning.main", fontSize: "1.1rem" }} />
                              <Typography variant="body2" color="text.secondary">
                                {warning.message}
                              </Typography>
                            </Box>
                          </Box>
                        ))}
                      </Box>
                    )}
                    <Divider sx={{ my: 2 }} />
                  </Box>
                )}
              {seriesIds.map((seriesId) => {
                const beforeAppts = (allBefore?.find((item: any) => item.seriesId === seriesId)
                  ?.appointments ?? []) as any[];
                const afterAppts = (allAfter?.find((item: any) => item.seriesId === seriesId)
                  ?.appointments ?? []) as any[];
                const { changedBefore, changedAfter, unchangedCount } = filterChanged(beforeAppts, afterAppts);

                // If no changes, show all appointments; otherwise show only changed ones
                const hasChanges = changedBefore.length > 0 || changedAfter.length > 0;
                const displayBefore = hasChanges ? changedBefore : beforeAppts;
                const displayAfter = hasChanges ? changedAfter : afterAppts;

                const seriesLabel: any = beforeAppts[0]?.timeSlots?.[0]?.therapyRx?.rx?.rxSeries;
                return (
                  <div key={seriesId} style={{ marginBottom: "2rem" }}>
                    <h2 style={{ textAlign: "center" }}>Behandlungsserien- {seriesLabel?.label}</h2>

                    <Grid container spacing={0}>
                      {/* Column headers */}
                      <Grid item xs={12} md={6}>
                        <h3 style={{ textAlign: "center", marginBottom: 0 }}>Vor Neuordnung</h3>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <h3 style={{ textAlign: "center", marginBottom: 0 }}>Nach Neuordnung</h3>
                      </Grid>

                      {/* No changes banner */}
                      {!hasChanges && beforeAppts.length > 0 && (
                        <Grid item xs={12}>
                          <Box display="flex" justifyContent="center" sx={{ mb: 2 }}>
                            <Alert severity="info" sx={{ maxWidth: 500 }}>
                              Alle Termine sind bereits in der korrekten Reihenfolge zugeordnet.
                            </Alert>
                          </Box>
                        </Grid>
                      )}

                      {/* Unchanged summary banner - only when some changed */}
                      {hasChanges && unchangedCount > 0 && (
                        <Grid item xs={12}>
                          <Box display="flex" justifyContent="center" sx={{ mb: 2 }}>
                            <Box display="inline-flex" alignItems="center" justifyContent="center" gap={0.75} py={0.75} px={2}
                              sx={{ minWidth: 320, backgroundColor: "#f0f9ff", borderRadius: 2 }}>
                              <InfoOutlinedIcon sx={{ fontSize: "1rem", color: "#0288d1" }} />
                              <Typography variant="body1" sx={{ fontSize: 15, color: "#014361" }}>
                                {unchangedCount} {unchangedCount === 1 ? "Termin bleibt" : "Termine bleiben"} unverändert
                              </Typography>
                            </Box>
                          </Box>
                        </Grid>
                      )}

                      {/* BEFORE cards */}
                      <Grid item xs={12} md={6}>
                        <Box sx={{ maxWidth: "80%", mx: "auto" }}>
                          <TerminView
                            appointments={displayBefore as AppointmentDto[]}
                            patientId={patientId}
                            appointmentsLoading={isLoading}
                            isView={TerminViewConstants.ALL_APPOINTMENTS}
                          />
                        </Box>
                      </Grid>

                      {/* AFTER cards */}
                      <Grid item xs={12} md={6}>
                        <Box sx={{ maxWidth: "80%", mx: "auto" }}>
                          <TerminView
                            appointments={displayAfter as AppointmentDto[]}
                            patientId={patientId}
                            appointmentsLoading={isLoading}
                            isView={TerminViewConstants.ALL_APPOINTMENTS}
                          />
                        </Box>
                      </Grid>
                    </Grid>
                  </div>
                );
              })}
              </>
            )}
          </DialogContent>
          <DialogActions>
            {isLoading || isSwapLoading ? (
              <Button onClick={onClose} disabled={isSwapLoading} variant="text">
                Abbrechen
              </Button>
            ) : showPreviewErrors ? (
              <>
                <Button onClick={onClose} variant="text">
                  Abbrechen
                </Button>
                <Button onClick={handleForceSwap} variant="contained" color="warning">
                  Trotzdem fortfahren
                </Button>
              </>
            ) : showResult ? (
              <Button onClick={handleResultClose} variant="contained">
                Verstanden
              </Button>
            ) : (
              <>
                <Button onClick={onClose} disabled={isSwapLoading} variant="text">
                  Abbrechen
                </Button>
                {hasInitialErrors ? (
                  <Button onClick={handleForceSwap} variant="contained" color="warning" disabled={isSwapLoading}>
                    Trotzdem fortfahren
                  </Button>
                ) : (
                  <Button onClick={handleForceSwap} variant="contained" disabled={isSwapLoading}>
                    Neuordnung durchführen
                  </Button>
                )}
              </>
            )}
          </DialogActions>
        </Dialog>

        <PostSwapRxCompletionDialog
          open={completionDialogOpen}
          onClose={handleCompletionClose}
          completableRxs={completableRxs}
          patientId={patientId}
        />
      </>
    );
  }
);

export default PreviewSwapDialog;
